import { Modal } from "bootstrap";

/*
 * Il modale si apre da un link con href="#ancora-del-modale".
 * E' possibile precompilare un campo del form passando dei parametri:
 *   <a href="#richiedi-informazioni?tipologia=Suite">   (query nell'ancora)
 *   https://.../pagina/?tipologia=Suite#richiedi-informazioni  (query della pagina)
 * Per ogni parametro il campo viene cercato nel modale come
 * #chiave, #cf7-chiave, [name="chiave"] o [name="chiave[]"].
 * Il valore puo' essere sia il "value" che l'etichetta dell'opzione.
 */

const VALID_KEY = /^[\w-]+$/;

const findField = (modal, key) =>
  modal.querySelector(`[id="${key}"]`) ||
  modal.querySelector(`[id="cf7-${key}"]`) ||
  modal.querySelector(`[name="${key}"]`) ||
  modal.querySelector(`[name="${key}[]"]`);

const matches = (field, value) => {
  const wanted = value.trim().toLowerCase();
  return field.value.trim().toLowerCase() === wanted || (field.textContent || "").trim().toLowerCase() === wanted;
};

const setFieldValue = (modal, field, value) => {
  if (field.tagName === "SELECT") {
    const option = Array.from(field.options).find((opt) => matches(opt, value));
    if (!option) return false;
    field.value = option.value;
    return true;
  }

  if (field.type === "radio" || field.type === "checkbox") {
    const group = Array.from(modal.querySelectorAll(`[name="${field.name}"]`));
    const input = group.find((el) => matches(el, value));
    if (!input) return false;
    group.forEach((el) => {
      el.checked = el === input;
    });
    return true;
  }

  field.value = value;
  return true;
};

const resetFieldValue = (modal, field) => {
  if (field.tagName === "SELECT") {
    const option = Array.from(field.options).find((opt) => opt.defaultSelected) || field.options[0];
    field.value = option ? option.value : "";
    return;
  }

  if (field.type === "radio" || field.type === "checkbox") {
    modal.querySelectorAll(`[name="${field.name}"]`).forEach((el) => {
      el.checked = el.defaultChecked;
    });
    return;
  }

  field.value = field.defaultValue;
};

// Chiavi precompilate all'ultima apertura, per poterle ripristinare
// quando il modale viene riaperto da un link senza parametri.
const applied = new WeakMap();

const fillFields = (modal, params) => {
  (applied.get(modal) || []).forEach((key) => {
    const field = findField(modal, key);
    if (field) {
      resetFieldValue(modal, field);
      field.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });

  const keys = [];
  applied.set(modal, keys);

  params.forEach((value, key) => {
    if (!VALID_KEY.test(key)) return;

    const field = findField(modal, key);
    if (!field || !setFieldValue(modal, field, value)) return;

    keys.push(key);
    field.dispatchEvent(new Event("change", { bubbles: true }));
  });
};

// Da "#richiedi-informazioni?tipologia=Suite" ricava id e parametri.
const parseHash = (hash) => {
  const [id, query] = hash.replace("#", "").split("?");
  return { id, params: new URLSearchParams(query || "") };
};

const formCf7Modal = () => {
  const modals = document.querySelectorAll(".form-cf7-modal");
  if (!modals.length) return;

  // L'ancora del modale puo' essere usata anche come id di altri elementi
  // (es. l'ancora HTML di un bottone Gutenberg): teniamo quindi il riferimento
  // all'elemento del modale invece di ricercarlo con getElementById.
  const instances = {};
  modals.forEach((modal) => {
    instances[modal.id] = { element: modal, modal: new Modal(modal, { backdrop: false }) };
  });

  const open = (id, params) => {
    const instance = instances[id];
    if (!instance) return;

    fillFields(instance.element, params);
    instance.modal.show();
  };

  document.addEventListener("click", (e) => {
    const trigger = e.target.closest('a[href*="#"]');
    if (!trigger) return;

    const { id, params } = parseHash(trigger.hash);
    if (!id || !instances[id]) return;

    e.preventDefault();
    open(id, params);
  });

  // Apertura diretta da URL: i parametri possono stare nella query della
  // pagina oppure nell'ancora stessa.
  const { id, params } = parseHash(window.location.hash);
  if (id && instances[id]) {
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (!params.has(key)) params.append(key, value);
    });
    open(id, params);
  }
};

export default formCf7Modal;
